import clsx from 'clsx';
import React from 'react';
import { SanityBlock, SanityFigure, SanityLink } from '../../../../graphql-types';
import { Typography } from '../atoms/typography';
import { RichText } from '../molecules/rich-text';

import { Container } from '../atoms/container';
import { SanityLinkList } from '../molecules/sanity-link-list';
import { SanityImage } from '../molecules/sanity-image';
import { ImageWithCaption } from '../molecules/image-with-caption';

export type Layout = 'image-left' | 'image-right' | 'image-top' | 'image-bottom';

type TitleSize = 'default' | 'large';
type Orientation = 'landscape' | 'portrait';

type TextImageBlockProps = {
    className?: string;
    layout?: Layout;
    title: string;
    description?: SanityBlock[];
    figure?: SanityFigure;
    links?: SanityLink[];
    background?: 'dark' | 'light' | 'np-green';
    orientation?: Orientation;
    titleSize?: TitleSize;
};

export function TextImageBlock({
    figure,
    title,
    description,
    links,
    layout = 'image-left',
    background = 'light',
    titleSize = 'default',
    orientation = 'landscape',
    className,
}: TextImageBlockProps) {
    const isFullSizeImage = layout === 'image-top' || layout === 'image-bottom';

    const isImageContain = !!figure?.contain;

    return (
        <Container
            className={clsx(className, 'grid gap-8 lg:gap-y-16', {
                'lg:grid-cols-2 lg:gap-x-16 xl:gap-x-20': !isFullSizeImage,
            })}
        >
            {figure ? (
                <ImageWithCaption
                    caption={figure.caption}
                    className={clsx('flex flex-col items-start', {
                        'lg:order-2 ': layout === 'image-right' || layout === 'image-bottom',
                    })}
                >
                    <SanityImage
                        className={clsx({
                            'w-full h-full': !isFullSizeImage,
                            'aspect-[4/3]': !isFullSizeImage && orientation === 'landscape',
                            'aspect-[3/4]': !isFullSizeImage && orientation === 'portrait',
                            'aspect-[4/3] lg:aspect-video xl:aspect-[16/6.5] w-full':
                                isFullSizeImage,
                            'object-contain': isImageContain,
                            'object-cover': !isImageContain,
                        })}
                        image={figure}
                        sizes={
                            isFullSizeImage
                                ? '(max-width: 1728px) 100vw,  1600px'
                                : '(max-width: 1024px) 100vw, (max-width: 1728px) 50vw,  800px'
                        }
                    />
                </ImageWithCaption>
            ) : null}
            <div
                className={clsx('lg:self-center', {
                    'lg:grid lg:grid-cols-2 lg:gap-x-16 xl:gap-x-20': isFullSizeImage,
                })}
            >
                <Typography
                    variant={titleSize === 'large' ? 'hd-3xl' : 'hd-lg'}
                    desktopVariant={titleSize === 'large' ? 'hd-4xl' : 'hd-lg'}
                    as="h2"
                >
                    {titleSize === 'large' ? title : <strong>{title}</strong>}
                </Typography>
                <div>
                    {description ? (
                        <RichText
                            value={description}
                            className={clsx({
                                'mt-3 lg:mt-0': !!title && isFullSizeImage,
                                'md:mt-4 lg:mt-6': !isFullSizeImage && !!title,
                            })}
                        />
                    ) : null}
                    <SanityLinkList
                        links={links}
                        size="md"
                        className="mt-8"
                        isBackgroundDark={background === 'dark'}
                    />
                </div>
            </div>
        </Container>
    );
}
